import React, { useState } from 'react'

import { Card, CardMedia, CardContent, IconButton, Typography, Chip, Box } from '@mui/material'

import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import { IClip } from '../../../api/Data'

import UpgradeTag from '../../../components/common/UpgradeTag'

import moment from '../../../utils/Moment'
import { formatNumber } from '../../../utils/sharedFunctions'

const CurateClipCard = (props: IProps) => {
  // Props
  const { userId, clip, clipsSelected, setShowClipModal, handleFavorite, addFilterTag, handleClipSelection, votes, handleVote } = props

  // States
  const [showSelectBox, setShowSelectBox] = useState(false)

  const { id, createdAt, isFavorite, contributor, imageLowResUrl, videoLowResUrl, isRestricted } = clip
  
  const isClipSelected = clipsSelected?.map((el) => el.id).includes(clip.id)

  const hasUserVoted = () => {
    return votes.includes(userId)
  }

  return (
    <Card
      onMouseEnter={() => setShowSelectBox(true)}
      onMouseLeave={() => setShowSelectBox(false)}
      sx={{ position: 'relative' }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component={videoLowResUrl ? 'video' : 'img'}
          image={!videoLowResUrl ? imageLowResUrl : undefined}
          src={videoLowResUrl ? videoLowResUrl : undefined}
          onMouseEnter={(e: React.MouseEvent<HTMLVideoElement>) => {
            if (videoLowResUrl) {
              (e.currentTarget as HTMLVideoElement).play();
            }
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLVideoElement>) => {
            if (videoLowResUrl) {
              (e.currentTarget as HTMLVideoElement).pause();
              (e.currentTarget as HTMLVideoElement).currentTime = 0;
            }
          }}
          onClick={() => setShowClipModal(clip)}
          sx={{
            aspectRatio: '16/9',
            cursor: 'pointer'
          }}
        />
        {
          isRestricted && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1}
              sx={{
                position: 'absolute',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                textAlign: 'center',
                color: '#ffffff'
              }}
            >
              To view this clip<br />
              please upgrade your plan
              <UpgradeTag text="Upgrade your plan to collect more video clips" />
            </Box>
          )
        }
      </Box>
      <Box display="flex" width="100%" justifyContent="space-between" alignItems="center" sx={{ position: 'absolute', top: 0, left: 0 }}>
        <IconButton disabled={!!isRestricted} onClick={() => handleFavorite(id)} sx={{ color: isFavorite ? '#ff0000' : '#ffffff', padding: '8px' }}>
          {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </IconButton>
        {(showSelectBox || isClipSelected) && (
          <IconButton onClick={() => handleClipSelection(clip)} sx={{ color: isClipSelected ? '#6b50ff' : '#ffffff' }}>
            {isClipSelected ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
          </IconButton>
        )}
      </Box>
      <CardContent sx={{ minHeight: '120px', padding: '10px !important' }}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" width="100%" justifyContent="space-between">
            <Box display="flex" flexDirection="column">
              <Typography variant="body2" color="textSecondary">
                {moment(createdAt).format('ll')}
              </Typography>
              <Typography variant="body2">
                {contributor || 'unknown'}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center" sx={{ border: '1px solid #7f7f7f', borderRadius: '5px', padding: '0.25rem 0.5rem' }}>
              <IconButton disabled={!!isRestricted} onClick={() => handleVote(clip, !hasUserVoted())} sx={{ padding: 0, color: hasUserVoted() ? '#6b50ff' : 'default' }}>
                {hasUserVoted() ? <KeyboardArrowUpIcon /> : <KeyboardArrowUpIcon />}
              </IconButton>
              <Typography variant="body2">
                {formatNumber(votes.length)}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {clip.tags.map((tag: string) => (
              <Chip
                key={tag}
                label={tag}
                variant="outlined"
                disabled={!!isRestricted}
                onClick={() => addFilterTag(tag)}
                sx={{
                  color: '#6b50ff',
                  borderColor: '#6b50ff',
                  height: '24px',
                  borderRadius: '4px',
                  padding: '0 8px',
                  fontSize: '0.75rem'
                }}
              />
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

interface IProps {
  userId: number
  clip: IClip
  clipsSelected: IClip[]
  setShowClipModal: (arg: IClip | null) => void
  handleFavorite: (arg: number) => void
  addFilterTag: (arg: string) => void
  handleClipSelection: (arg: IClip) => void
  votes: number[]
  handleVote: (clip: IClip, isVote: boolean) => void
}

export default CurateClipCard
